import { useEffect, useMemo, useState } from "react";
import { Checkbox, Divider, Modal, Select, Tooltip, Typography } from "antd";
import { BiInfoCircle } from "react-icons/bi";
import { useApiCall } from "../../services/api";
import SearchInput from "../Forms/SearchInput";
import PrimaryButton from "../Forms/PrimaryButton";

const { Title, Paragraph } = Typography;

const normalizeArray = (value) => (Array.isArray(value) ? value : []);

export default function ManagePermissions({ isOpen, onClose }) {
  const { postApi, getApi, loading } = useApiCall();
  const [searchText, setSearchText] = useState("");
  const [salesPersons, setSalesPersons] = useState([]);
  const [permissionCatalog, setPermissionCatalog] = useState([]);
  const [permissionRows, setPermissionRows] = useState([]);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState("ADD");
  const [selectedSalesPersonId, setSelectedSalesPersonId] = useState(undefined);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);

  const existingSalesPersonIds = useMemo(() => permissionRows.map((item) => item.user_id), [permissionRows]);

  const permissionCatalogMap = useMemo(() => {
    return permissionCatalog.reduce((accumulator, item) => {
      accumulator[item.permission_id] = item;
      return accumulator;
    }, {});
  }, [permissionCatalog]);

  const salesPersonOptions = useMemo(() => {
    const currentId = selectedSalesPersonId;

    return normalizeArray(salesPersons)
      .filter((item) => {
        const itemId = item?.user_id ?? item?.id;
        if (editorMode === "ADD") {
          return !existingSalesPersonIds.some((value) => String(value) === String(itemId));
        }
        return true;
      })
      .map((item) => ({
        ...item,
        disabled: editorMode === "UPDATE" && String(item?.user_id ?? item?.id) !== String(currentId),
      }));
  }, [editorMode, existingSalesPersonIds, salesPersons, selectedSalesPersonId]);

  const filteredPermissionRows = useMemo(() => {
    const value = searchText.toLowerCase().trim();
    if (!value) return permissionRows;

    return permissionRows.filter((item) => {
      const permissionNames = normalizeArray(item.permissions)
        .map((permission) => permission.permission_name)
        .join(" ");
      return `${item.full_name || ""} ${permissionNames}`.toLowerCase().includes(value);
    });
  }, [permissionRows, searchText]);

  const fetchPermissions = async () => {
    const [permissionResponse, salesResponse, existingPermissionsResponse] = await Promise.all([
      postApi("rbac/get-list-permission", {}, false),
      getApi("sales/sales-person-dropdown-list"),
      getApi("rbac/list"),
    ]);

    setPermissionRows(normalizeArray(permissionResponse?.data));
    setSalesPersons(normalizeArray(salesResponse?.data));
    setPermissionCatalog(normalizeArray(existingPermissionsResponse?.data));
  };

  useEffect(() => {
    if (!isOpen) return;

    setSearchText("");
    setEditorOpen(false);
    setEditorMode("ADD");
    setSelectedSalesPersonId(undefined);
    setSelectedPermissionIds([]);
    fetchPermissions();
  }, [isOpen]);

  const openAddPermissionModal = () => {
    setEditorMode("ADD");
    setSelectedSalesPersonId(undefined);
    setSelectedPermissionIds([]);
    setEditorOpen(true);
  };

  const openEditPermissionModal = (record) => {
    setEditorMode("UPDATE");
    setSelectedSalesPersonId(record?.user_id);
    setSelectedPermissionIds(normalizeArray(record?.permissions).map((permission) => permission.permission_id));
    setEditorOpen(true);
  };

  const handlePermissionToggle = (permissionId, checked) => {
    setSelectedPermissionIds((prev) => {
      if (checked) {
        return prev.includes(permissionId) ? prev : [...prev, permissionId];
      }

      return prev.filter((value) => value !== permissionId);
    });
  };

  const handleSavePermissions = async () => {
    if (!selectedSalesPersonId) return;

    const endpoint = "rbac/add-update-permission";
    const payload = {
      user_id: selectedSalesPersonId,
      permission_id: selectedPermissionIds,
    };

    const response = await postApi(endpoint, payload, true);
    if (response?.status) {
      setEditorOpen(false);
      fetchPermissions();
    }
  };

  return (
    <>
      <Modal className="form-css" open={isOpen} onCancel={onClose} footer={null} width={600} destroyOnClose>
        <div className="flex items-start justify-between gap-4">
          <div>
            <Title level={2} className="text-3xl font-semibold text-gray-800 font-abhaya m-0">
              Manage Permissions
            </Title>
          </div>
        </div>

        <div className="flex items-center gap-4 mt-4">
          <div className="flex-1">
            <SearchInput value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Search for permissions" />
          </div>
          <PrimaryButton btnType="secondary" text="Add Permission" onClick={openAddPermissionModal} />
        </div>

        <div className="mt-6 space-y-3">
          <div className="w-full px-4 text-left flex">
            <span className="w-[12%] text-left font-medium text-gray-700">#</span>
            <span className="w-[88%] text-gray-900 text-left">Salesperson</span>
          </div>
          <Divider />

          {filteredPermissionRows.length > 0 ? (
            filteredPermissionRows.map((item, index) => {
              const permissionNames = normalizeArray(item.permissions)
                .map((permission) => permission.permission_name)
                .join(", ");

              return (
                <div
                  key={item.user_id ?? index}
                  className="flex justify-between items-start px-4 py-3 rounded-md bg-white hover:bg-[#FBEEEC] cursor-pointer"
                  onClick={() => openEditPermissionModal(item)}
                >
                  <div className="w-full text-left flex items-start gap-3">
                    <span className="font-medium text-gray-700 w-[12%] pt-1">{index + 1}</span>
                    <div className="w-[88%] text-left">
                      <span className="block font-semibold text-gray-900">{item.full_name || "-"}</span>
                      <span className="block text-sm text-gray-500 mt-1">{permissionNames || "No permissions assigned"}</span>
                    </div>
                  </div>
                </div>
              );
            })
          ) : (
            <div className="flex justify-center items-center py-10">
              <span className="text-gray-500">Sorry, no permissions found</span>
            </div>
          )}
        </div>
      </Modal>

      <Modal className="form-css-2" open={editorOpen} onCancel={() => setEditorOpen(false)} footer={null} width={760} destroyOnClose>
        <div className="flex flex-col gap-4">
          <div>
            <Title level={2} className="text-3xl font-semibold text-gray-800 font-abhaya m-0">
              {editorMode === "ADD" ? "Add Permissions" : "Update Permissions"}
            </Title>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Salesperson</label>
            <Select
              className="w-full"
              placeholder="Select Salesperson"
              value={selectedSalesPersonId}
              onChange={setSelectedSalesPersonId}
              disabled={editorMode === "UPDATE"}
              showSearch
              optionFilterProp="label"
              options={salesPersonOptions.map((item) => ({
                value: item.user_id ?? item.id,
                label: item.full_name,
                disabled: item.disabled,
              }))}
            />
          </div>

          <div className="overflow-y-auto pr-1 space-y-3">
            {permissionCatalog.map((permission) => {
              const isChecked = selectedPermissionIds.includes(permission.permission_id);

              return (
                <div
                  key={permission.permission_id}
                  className="flex items-start justify-between gap-3 rounded-md border border-gray-100 px-4 py-3 bg-white"
                >
                  <div className="flex items-start gap-3">
                    <Checkbox checked={isChecked} onChange={(e) => handlePermissionToggle(permission.permission_id, e.target.checked)} />
                    <div>
                      <span className="block font-semibold text-gray-900">{permission.permission_name}</span>
                    </div>
                  </div>

                  <Tooltip title={permission.permission_desc}>
                    <BiInfoCircle className="text-[#A33434] mt-0.5 shrink-0" size={22} />
                  </Tooltip>
                </div>
              );
            })}
          </div>

          <div className="w-full flex justify-center gap-3 pt-1">
            <PrimaryButton
              className="me-[30px]"
              text={editorMode === "ADD" ? "Add" : "Update"}
              onClick={handleSavePermissions}
              loading={loading}
              disabled={!selectedSalesPersonId || (editorMode === "ADD" && selectedPermissionIds.length === 0)}
            />
          </div>
        </div>
      </Modal>
    </>
  );
}
